/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState } from "react";
import { Loading } from "./AllReports";
import produce from "immer";

const PatientsTable = ({ data, initialLoad, mergePatients, onMergePatients, sortingFunction }) => {
  const [lastNameOrder, setLastNameOrder] = useState(null);
  const [firstNameOrder, setFirstNameOrder] = useState(null);
  const [mnrOrder, setMnrOrder] = useState(null);

  const onMerge = (e, type, data) => {
    const { checked } = e.target;
    if (checked)
      onMergePatients((prev) =>
        produce(prev, (draft) => {
          draft[type] = data;
        })
      );
    else
      onMergePatients((prev) =>
        produce(prev, (draft) => {
          draft[type] = null;
        })
      );
  };

  const handleSort = (field) => {
    switch (field) {
      case "lastName":
        setFirstNameOrder(null);
        setMnrOrder(null);
        if (lastNameOrder === null) {
          setLastNameOrder(true);
          sortingFunction(["family", "id"]);
        } else if (lastNameOrder) {
          setLastNameOrder(false);
          sortingFunction(["-family", "id"]);
        } else {
          setLastNameOrder(null);
          sortingFunction(null);
        }
        break;
      case "firstName":
        setLastNameOrder(null);
        setMnrOrder(null);
        if (firstNameOrder === null) {
          setFirstNameOrder(true);
          sortingFunction(["name", "id"]);
        } else if (firstNameOrder) {
          setFirstNameOrder(false);
          sortingFunction(["-name", "id"]);
        } else {
          setFirstNameOrder(null);
          sortingFunction(null);
        }
        break;
      case "mrn":
        setLastNameOrder(null);
        setFirstNameOrder(null);
        if (mnrOrder === null) {
          setMnrOrder(true);
          sortingFunction(["patientLocation.mrn", "id"]);
        } else if (mnrOrder) {
          setMnrOrder(false);
          sortingFunction(["-patientLocation.mrn", "id"]);
        } else {
          setMnrOrder(null);
          sortingFunction(null);
        }
        break;
    }
  };

  return (
    <div className="contentTbl">
      <table id="patients-merge-table" className="overview">
        <thead>
          <tr>
            <th style={{ width: "5%" }}>Date Added</th>
            <th style={{ width: "4%" }}>Status</th>
            <th
              style={{ width: "18%" }}
              className={
                lastNameOrder === null ? "sortDisable" : lastNameOrder ? "headerSortUp" : "headerSortDown"
              }
            >
              <a href="#" onClick={() => handleSort("lastName")}>
                Last Name
              </a>
            </th>
            <th
              style={{ width: "18%" }}
              className={
                firstNameOrder === null ? "sortDisable" : firstNameOrder ? "headerSortUp" : "headerSortDown"
              }
            >
              <a href="#" onClick={() => handleSort("firstName")}>
                First Name
              </a>
            </th>
            <th style={{ width: "4%" }}>DOB</th>
            <th style={{ width: "18%" }}>Following</th>
            <th style={{ width: "5%" }}>Gender</th>
            <th
              style={{ width: "5%" }}
              className={mnrOrder == null ? "sortDisable" : mnrOrder ? "headerSortUp" : "headerSortDown"}
            >
              <a href="#" onClick={() => handleSort("mrn")}>
                MRN
              </a>
            </th>
            <th style={{ width: "17%" }}>Locations</th>
            <th style={{ width: "3%" }}></th>
            <th style={{ width: "3%" }}></th>
          </tr>
        </thead>

        {initialLoad ? (
          <Loading />
        ) : data.length > 0 ? (
          <tbody>
            {data.map(({ insurance, location, patient, ...item }, index) => (
              <tr key={item.id}>
                <td style={{ width: "5%" }}>{new Date(item.createdAt).toLocaleDateString()}</td>
                <td style={{ width: "4%" }}>{insurance?.status}</td>
                <td style={{ width: "18%" }}>{item.family}</td>
                <td style={{ width: "18%" }}>{item.name}</td>
                <td style={{ width: "4%" }}>{new Date(item.dob).toLocaleDateString()}</td>
                <td style={{ width: "18%" }}>
                  {item.followingPhysician ? item.followingPhysician.family + "," : "-"}{" "}
                  {item.followingPhysician ? item.followingPhysician.name : " "}
                </td>
                <td style={{ width: "5%" }}>{item.gender}</td>
                <td style={{ width: "5%" }}>
                  {Array.isArray(item?.patientLocation) &&
                    item?.patientLocation?.map((it) => it?.mrn).join(",")}
                </td>
                <td style={{ width: "17%" }}>
                  {Array.isArray(item?.patientLocation) &&
                    item.patientLocation?.map((it) => it?.location?.name).join(",")}
                </td>
                <td style={{ width: "3%" }}>
                  <label className="form-li" style={{ display: "inline-flex", alignItems: "center" }}>
                    <input
                      disabled={item.patientState != "ACTIVE"}
                      type="checkbox"
                      className="chk"
                      checked={item.id === mergePatients?.from?.id}
                      onChange={(e) => onMerge(e, "from", item)}
                    />
                    <span
                      className={` ${item.patientState != "ACTIVE" ? "chkmrk chkdisabled" : "chkmrk"}`}
                    ></span>
                    <span className="lbl-txt" style={{ marginLeft: 5 }}>
                      Merge From
                    </span>
                  </label>
                </td>
                <td style={{ width: "3%" }}>
                  <label className="form-li" style={{ display: "inline-flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      className="chk"
                      checked={item.id === mergePatients?.to?.id}
                      onChange={(e) => onMerge(e, "to", item)}
                    />
                    <span className="chkmrk"></span>
                    <span className="lbl-txt" style={{ marginLeft: 5 }}>
                      Merge To
                    </span>
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        ) : null}
      </table>
      {data.length === 0 && !initialLoad ? (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
            fontSize: 18,
          }}
        >
          No patients
        </div>
      ) : null}
    </div>
  );
};

export default PatientsTable;
